/** Type générique définissant la signature d'une fonction gestionnaire d'événement. */
type EventHandler<T> = (event: T) => void;

/** Type générique pour définir les événements possibles et leurs données associées. */
type EventMap = { [K: string]: any; }

/**
 * Classe permettant de gérer des événements personnalisés avec des gestionnaires associés.
 * 
 * @template TEventMap Type définissant la map des événements et leurs données.
 */
export class EventDispatcher<TEventMap extends EventMap> {
    /** Stocke les gestionnaires d'événements par nom. */
    private listeners: { [K in keyof TEventMap]?: EventHandler<TEventMap[K]>[] } = {};

    /**
     * Ajoute un gestionnaire d'événements pour un événement spécifié.
     * 
     * @param {K} event Le nom de l'événement.
     * @param {EventHandler<TEventMap[K]>} handler La fonction à appeler lors de l'événement.
     * 
     * @return {this}
     */
    public on<K extends keyof TEventMap>(event: K, handler: EventHandler<TEventMap[K]>): this {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }

        this.listeners[event]?.push(handler);

        return this;
    }

    /**
     * Retire un gestionnaire d'événements spécifié pour un événement donné.
     * 
     * @param {K} event Le nom de l'événement.
     * @param {EventHandler<TEventMap[K]>} handler La fonction à retirer.
     * 
     * @return {this}
     */
    public off<K extends keyof TEventMap>(event: K, handler: EventHandler<TEventMap[K]>): this {
        if (!this.listeners[event]) {
            return this;
        }

        this.listeners[event] = this.listeners[event]?.filter(h => h !== handler);

        return this;
    }

    /**
     * Déclenche un événement spécifié avec des données associées, en appelant tous les gestionnaires enregistrés pour cet événement.
     * 
     * @param {K} event Le nom de l'événement à déclencher.
     * @param {TEventMap[K]} data Les données à passer aux gestionnaires de l'événement.
     * 
     * @return {this}
     */
    public dispatch<K extends keyof TEventMap>(event: K, data: TEventMap[K]): this {
        const handlers = this.listeners[event];

        if (handlers) {
            handlers.forEach(handler => handler(data));
        }

        return this;
    }
}
